/**
 * ============================================================
 *  FACET FILTERS FORM — Fixed & Premium Edition
 *  Fixes:
 *   1. html.innerHTML hatası (string üzerinde .innerHTML çağrısı)
 *   2. event.srcElement deprecation → event.target kullanımı
 *   3. Null-safe optional chaining (?.) eklemeleri
 *   4. renderSectionFromFetch'te hata yakalama
 *   5. updateURLHash'te string concat yerine template literal
 *   6. debounce / onKeyUpEscape undefined olduğunda fallback
 * ============================================================
 */

/* ── Utility: debounce (tema global'de tanımlı değilse fallback) ─────────── */
if (typeof debounce !== 'function') {
  window.debounce = function debounce(fn, delay) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  };
}

/* ── Utility: onKeyUpEscape (tema global'de tanımlı değilse fallback) ────── */
if (typeof onKeyUpEscape !== 'function') {
  window.onKeyUpEscape = function onKeyUpEscape(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;
    const openDetails = event.target.closest('details[open]');
    if (!openDetails) return;
    openDetails.removeAttribute('open');
    openDetails.querySelector('summary')?.focus();
  };
}

/* ══════════════════════════════════════════════════════════════
   FacetFiltersForm
═══════════════════════════════════════════════════════════════ */
class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    if (facetForm) {
      facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
    }

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) {
      facetWrapper.addEventListener('keyup', onKeyUpEscape);
    }
  }

  /* ── Listeners ──────────────────────────────────────────── */
  static setListeners() {
    window.addEventListener('popstate', (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    });
  }

  /* ── Active facet badge toggle ──────────────────────────── */
  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((el) => {
      el.classList.toggle('disabled', disable);
    });
  }

  /* ── Core render orchestrator ───────────────────────────── */
  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;

    const sections        = FacetFiltersForm.getSections();
    const countContainer  = document.getElementById('ProductCount');
    const countDesktop    = document.getElementById('ProductCountDesktop');
    const productGrid     = document.getElementById('ProductGridContainer');

    /* Loading-on */
    document
      .querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner')
      .forEach((s) => s.classList.remove('hidden'));

    productGrid?.querySelector('.collection')?.classList.add('loading');
    countContainer?.classList.add('loading');
    countDesktop?.classList.add('loading');

    sections.forEach(({ section }) => {
      if (!section) return;                          // guard: data-id yok ise çık
      const url          = `${window.location.pathname}?section_id=${section}&${searchParams}`;
      const filterDataUrl = (item) => item.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  /* ── Fetch → parse → render ─────────────────────────────── */
  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);
        return response.text();
      })
      .then((responseText) => {
        /* Cache the raw HTML string */
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          { html: responseText, url },
        ];
        FacetFiltersForm.renderFilters(responseText, event);
        FacetFiltersForm.renderProductGridContainer(responseText);
        FacetFiltersForm.renderProductCount(responseText);

        /* FIX: html bir string; .innerHTML değil, doğrudan string geç */
        if (typeof initializeScrollAnimationTrigger === 'function') {
          initializeScrollAnimationTrigger(responseText);
        }
      })
      .catch((err) => {
        console.error('[FacetFiltersForm] renderSectionFromFetch hatası:', err);
        /* Loading-off (hata durumunda) */
        document
          .querySelectorAll('.loading__spinner')
          .forEach((s) => s.classList.add('hidden'));
        document
          .getElementById('ProductGridContainer')
          ?.querySelector('.collection')
          ?.classList.remove('loading');
        document.getElementById('ProductCount')?.classList.remove('loading');
        document.getElementById('ProductCountDesktop')?.classList.remove('loading');
      });
  }

  /* ── Cache'ten render ───────────────────────────────────── */
  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);

    /* FIX: aynı düzeltme cache path için de */
    if (typeof initializeScrollAnimationTrigger === 'function') {
      initializeScrollAnimationTrigger(html);
    }
  }

  /* ── Ürün grid'ini güncelle ──────────────────────────────── */
  static renderProductGridContainer(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const newGrid    = parsedHTML.getElementById('ProductGridContainer');
    if (!newGrid) return;

    const grid = document.getElementById('ProductGridContainer');
    if (!grid) return;

    grid.innerHTML = newGrid.innerHTML;
    grid.querySelectorAll('.scroll-trigger').forEach((el) => {
      el.classList.add('scroll-trigger--cancel');
    });
  }

  /* ── Ürün sayacını güncelle ──────────────────────────────── */
  static renderProductCount(html) {
    const parsedHTML      = new DOMParser().parseFromString(html, 'text/html');
    const newCount        = parsedHTML.getElementById('ProductCount');
    if (!newCount) return;

    const count           = newCount.innerHTML;
    const container       = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');

    if (container) {
      container.innerHTML = count;
      container.classList.remove('loading');
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }

    document
      .querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner')
      .forEach((s) => s.classList.add('hidden'));
  }

  /* ── Filtre panellerini güncelle ─────────────────────────── */
  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const fromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const fromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    /* Artık döndürülmeyen facet'leri kaldır */
    Array.from(fromDom).forEach((el) => {
      if (!Array.from(fromFetch).some(({ id }) => el.id === id)) el.remove();
    });

    const matchesId = (el) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? el.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(fromFetch).filter((el) => !matchesId(el));
    const countsToRender = Array.from(fromFetch).find(matchesId);

    facetsToRender.forEach((elToRender, index) => {
      const current = document.getElementById(elToRender.id);
      if (current) {
        current.innerHTML = elToRender.innerHTML;
      } else {
        if (index > 0) {
          const prev = facetsToRender[index - 1];
          if (elToRender.className === prev.className) {
            document.getElementById(prev.id)?.after(elToRender);
            return;
          }
        }
        if (elToRender.parentElement) {
          document
            .querySelector(`#${elToRender.parentElement.id} .js-filter`)
            ?.before(elToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestFilter   = event?.target.closest('.js-filter');
      const closestFilterID = closestFilter?.id;

      if (closestFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, closestFilter);
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestFilterID));

        const newFacetEl = document.getElementById(closestFilterID);
        const selector   = newFacetEl?.classList.contains('mobile-facets__details')
          ? '.mobile-facets__close-button'
          : '.facets__summary';
        const elToFocus  = newFacetEl?.querySelector(selector);
        const isText     = event?.target.getAttribute('type') === 'text';
        if (elToFocus && !isText) elToFocus.focus();
      }
    }
  }

  /* ── Aktif filtre rozet satırını güncelle ────────────────── */
  static renderActiveFacets(html) {
    ['.active-facets-mobile', '.active-facets-desktop'].forEach((sel) => {
      const src = html.querySelector(sel);
      if (!src) return;
      const trg = document.querySelector(sel);
      if (trg) trg.innerHTML = src.innerHTML;
    });
    FacetFiltersForm.toggleActiveFacets(false);
  }

  /* ── Mobil yardımcı elemanları güncelle ──────────────────── */
  static renderAdditionalElements(html) {
    ['.mobile-facets__open', '.mobile-facets__count', '.sorting'].forEach((sel) => {
      const src = html.querySelector(sel);
      const trg = document.querySelector(sel);
      if (src && trg) trg.innerHTML = src.innerHTML;
    });

    const menuDrawer = document
      .getElementById('FacetFiltersFormMobile')
      ?.closest('menu-drawer');
    if (menuDrawer && typeof menuDrawer.bindEvents === 'function') {
      menuDrawer.bindEvents();
    }
  }

  /* ── Desktop sayaçlarını güncelle ────────────────────────── */
  static renderCounts(source, target) {
    ['.facets__summary', '.facets__header'].forEach((sel) => {
      const src = source.querySelector(sel);
      const trg = target.querySelector(sel);
      if (src && trg) trg.outerHTML = src.outerHTML;
    });

    const srcWrap = source.querySelector('.facets-wrap');
    const trgWrap = target.querySelector('.facets-wrap');
    if (srcWrap && trgWrap) {
      const isShowingMore = Boolean(
        target.querySelector('show-more-button .label-show-more.hidden')
      );
      if (isShowingMore) {
        srcWrap
          .querySelectorAll('.facets__item.hidden')
          .forEach((item) => item.classList.replace('hidden', 'show-more-item'));
      }
      trgWrap.outerHTML = srcWrap.outerHTML;
    }
  }

  /* ── Mobil sayaçlarını güncelle ──────────────────────────── */
  static renderMobileCounts(source, target) {
    const src = source.querySelector('.mobile-facets__list');
    const trg = target?.querySelector('.mobile-facets__list');
    if (src && trg) trg.outerHTML = src.outerHTML;
  }

  /* ── URL hash güncelle ───────────────────────────────────── */
  static updateURLHash(searchParams) {
    /* FIX: concat yerine template literal — boş string birleştirme hatası giderildi */
    const qs = searchParams ? `?${searchParams}` : '';
    history.pushState({ searchParams }, '', `${window.location.pathname}${qs}`);
  }

  /* ── Section id al ───────────────────────────────────────── */
  static getSections() {
    return [
      {
        section: document.getElementById('product-grid')?.dataset.id,
      },
    ];
  }

  /* ── Form → query string ─────────────────────────────────── */
  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');

    /* FIX: event.srcElement deprecated → event.target kullan */
    if (event.target.className === 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms    = [];
      const isMobile = event.target.closest('form')?.id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (
            form.id === 'FacetSortForm' ||
            form.id === 'FacetFiltersForm' ||
            form.id === 'FacetSortDrawerForm'
          ) {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });

      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const href = event.currentTarget.href;
    const url  = href.includes('?') ? href.slice(href.indexOf('?') + 1) : '';
    FacetFiltersForm.renderPage(url);
  }
}

/* ── Static init ─────────────────────────────────────────────── */
FacetFiltersForm.filterData          = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev    = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

/* ══════════════════════════════════════════════════════════════
   PriceRange
═══════════════════════════════════════════════════════════════ */
class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((el) => {
      el.addEventListener('change', this.onRangeChange.bind(this));
      el.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;
    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const [minInput, maxInput] = this.querySelectorAll('input');
    if (maxInput.value)   minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value)   maxInput.setAttribute('data-min', minInput.value);
    if (!minInput.value)  maxInput.setAttribute('data-min', 0);
    if (!maxInput.value)  minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min   = Number(input.getAttribute('data-min'));
    const max   = Number(input.getAttribute('data-max'));
    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

/* ══════════════════════════════════════════════════════════════
   FacetRemove
═══════════════════════════════════════════════════════════════ */
class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    if (!facetLink) return;

    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form =
      this.closest('facet-filters-form') ||
      document.querySelector('facet-filters-form');
    form?.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);